import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, fmtEUR, formatApiError } from "../lib/api";
import { Loader2, Pencil, MousePointer2, Undo2, RotateCcw, Lock, Unlock, FileCheck2, AlertTriangle, Map, Columns3, Plus, Trash2, Layers } from "lucide-react";
import { PlannerCanvas } from "../components/planner/PlannerCanvas";
import { SegmentTable } from "../components/planner/SegmentTable";
import { computeLayout, emptyProject, migrateProject, newSection, setSegmentLength, setVertexAngle, flipVertex, removeSegment, profileDims, GATE_DEFAULTS, deriveWerk, segmentsOf, ansichtLayout, DEFAULT_WERK, LUNAR_LAMELLA, gatePostProfiles, isPostless, allowedPostProfiles } from "../lib/planner";
import { WerkPanel } from "../components/planner/WerkPanel";
import { AnsichtCanvas } from "../components/planner/AnsichtCanvas";
import { AnsichtTable } from "../components/planner/AnsichtTable";
import { AnsichtPanel } from "../components/planner/AnsichtPanel";
import { NumInput } from "../components/NumInput";

const DRAFT_KEY = "mak-zaunplan-draft";
const selCls = "w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm";
const numCls = "w-full px-3 py-2 bg-white border border-zinc-300 rounded-sm text-sm font-mono focus:border-black focus:ring-1 focus:ring-black";
const Label = ({ children }) => <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">{children}</label>;

export default function ZaunPlaner() {
  const navigate = useNavigate();
  const location = useLocation();
  const quoteId = location.state?.quoteId || null;
  const inquiry = location.state?.inquiry || null;
  const [pricelist, setPricelist] = useState(null);
  const [project, setProject] = useState(null);
  const [mode, setMode] = useState("draw");
  const [ortho, setOrtho] = useState(true);
  const [selectedSeg, setSelectedSeg] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/pricelist").then((r) => {
      setPricelist(r.data);
      const fromState = location.state?.plan;
      let draft = null;
      try { draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null"); } catch { draft = null; }
      const proj = fromState ? migrateProject(fromState, r.data) : draft ? migrateProject(draft, r.data) : emptyProject(r.data);
      setProject(proj);
      const sec0 = proj.sections[proj.active];
      if (sec0?.points?.length) setMode("edit");
    }).catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message));
    // eslint-disable-next-line
  }, []);
  useEffect(() => { if (project) localStorage.setItem(DRAFT_KEY, JSON.stringify(project)); }, [project]);

  const isAnsicht = project?.view === "ansicht";
  const sectionLayouts = useMemo(() => (project ? project.sections.map((s) => {
    const d = deriveWerk(s);
    return { sec: s, derived: d, layout: isAnsicht ? ansichtLayout(s) : computeLayout(d.plan) };
  }) : []), [project, isAnsicht]);

  if (error) return <div className="p-6 text-sm text-red-600" data-testid="planner-error">{error}</div>;
  if (!project || !pricelist) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin" /></div>;

  const active = project.active;
  const sec = project.sections[active];
  const cur = sectionLayouts[active];
  const layout = cur.layout;
  const derived = cur.derived;

  const upProj = (patch) => setProject((p) => ({ ...p, ...patch }));
  const mutSec = (fn) => setProject((p) => ({ ...p, sections: p.sections.map((s, i) => (i === p.active ? fn(s) : s)) }));
  const upSec = (patch) => mutSec((s) => ({ ...s, ...patch }));

  // --- Abschnitte ---
  const addSection = () => setProject((p) => {
    const prev = p.sections[p.active];
    const ns = newSection(pricelist, p.sections.length, { model: prev.model, variant: prev.variant, height: prev.height, postHeight: prev.postHeight, maxField: prev.maxField, posts: prev.posts });
    return { ...p, sections: [...p.sections, ns], active: p.sections.length };
  });
  const removeSection = (idx) => setProject((p) => {
    if (p.sections.length <= 1) return { ...p, sections: [newSection(pricelist, 0)], active: 0 };
    const sections = p.sections.filter((_, i) => i !== idx);
    return { ...p, sections, active: Math.max(0, Math.min(p.active > idx ? p.active - 1 : p.active, sections.length - 1)) };
  });
  const selectSection = (idx) => { upProj({ active: idx }); setSelectedSeg(null); setSelectedItem(null); setMode(project.sections[idx]?.points?.length ? "edit" : "draw"); };

  // --- Zeichnen (aktiver Abschnitt) ---
  const addPoint = (pt) => { if (!sec.closed) upSec({ points: [...sec.points, pt] }); };
  const movePoint = (i, pt) => upSec({ points: sec.points.map((p, j) => (j === i ? pt : p)) });
  const undo = () => upSec({ points: sec.points.slice(0, -1), closed: false, gates: sec.gates.filter((g) => g.seg < Math.max(0, sec.points.length - 2)) });
  const reset = () => { upSec({ points: [], closed: false, gates: [], ansicht: { items: [], wandStart: false, wandEnd: false } }); setMode("draw"); setSelectedSeg(null); setSelectedItem(null); };
  const setView = (v) => upProj({ view: v });
  const toggleClosed = () => { if (sec.points.length >= 3) { upSec({ closed: !sec.closed, gates: sec.closed ? sec.gates.filter((g) => g.seg < sec.points.length - 1) : sec.gates }); setMode("edit"); } };

  const ghosts = sectionLayouts.filter((_, i) => i !== active).map((x) => ({ layout: x.layout, color: x.sec.color, name: x.sec.name }));
  const fitPoints = project.sections.flatMap((s) => s.points || []);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8" data-testid="planner-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Planung</div>
        <h1 className="text-2xl md:text-3xl font-semibold text-zinc-900 tracking-tight">Zaunplaner</h1>
        <p className="text-sm text-zinc-600 mt-1">Mehrere Abschnitte zeichnen, Tore platzieren, Pfosten wählen – Felder und Stückliste werden automatisch berechnet.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="planner-view-toggle">
        <ToolBtn active={!isAnsicht} onClick={() => setView("grundriss")} icon={Map} label="Grundriss (Draufsicht) – Fundament + Alu-Pfosten" testId="planner-view-grundriss" />
        <ToolBtn active={isAnsicht} onClick={() => setView("ansicht")} icon={Columns3} label="Ansicht (Bokocrt) – Mauerpfeiler / Betonsäulen" testId="planner-view-ansicht" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <SectionList project={project} sectionLayouts={sectionLayouts} onSelect={selectSection} onAdd={addSection} onRemove={removeSection} onRename={(name) => upSec({ name })} />
          {isAnsicht ? (
            <>
              <AnsichtCanvas plan={sec} layout={layout} selected={selectedItem} onSelect={setSelectedItem} />
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-zinc-600 px-1" data-testid="ansicht-legend">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-zinc-200 border border-zinc-500 inline-block"/> Pfeiler / Sockel (Beton)</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-zinc-700 inline-block"/> Lamellen</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-zinc-900 inline-block"/> Alu-Pfosten</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-blue-600 inline-block"/> Tor</span>
              </div>
              <section>
                <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-700 pb-2 mb-3 border-b border-zinc-200">Pfeiler &amp; Felder – {sec.name} (von links nach rechts)</h2>
                <AnsichtTable plan={sec} layout={layout} selected={selectedItem} onSelect={setSelectedItem} onChange={upSec} pricelist={pricelist} />
              </section>
            </>
          ) : (
          <>
          <div className="flex flex-wrap items-center gap-2" data-testid="planner-toolbar">
            <ToolBtn active={mode === "draw"} onClick={() => setMode("draw")} icon={Pencil} label="Zeichnen" testId="planner-mode-draw" />
            <ToolBtn active={mode === "edit"} onClick={() => setMode("edit")} icon={MousePointer2} label="Bearbeiten / Verschieben" testId="planner-mode-edit" />
            <label className="flex items-center gap-1.5 text-xs text-zinc-700 ml-1 cursor-pointer">
              <input type="checkbox" checked={ortho} onChange={(e) => setOrtho(e.target.checked)} className="accent-black" data-testid="planner-ortho" /> 90°-Fang
            </label>
            <div className="flex-1" />
            <ToolBtn onClick={undo} icon={Undo2} label="Rückgängig" disabled={!sec.points.length} testId="planner-undo" />
            <ToolBtn onClick={toggleClosed} icon={sec.closed ? Unlock : Lock} label={sec.closed ? "Öffnen" : "Schließen"} disabled={sec.points.length < 3} testId="planner-close" />
            <ToolBtn onClick={reset} icon={RotateCcw} label="Neu" testId="planner-reset" />
          </div>
          <PlannerCanvas plan={sec} layout={layout} mode={mode} ortho={ortho} onAddPoint={addPoint} onMovePoint={movePoint} concrete={derived.concrete}
                         ghosts={ghosts} fitPoints={fitPoints}
                         onClose={() => { upSec({ closed: true }); setMode("edit"); }} selectedSeg={selectedSeg} onSelectSeg={setSelectedSeg} />
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-zinc-600 px-1" data-testid="planner-legend">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-zinc-900 inline-block"/> Eck-/Endpfosten</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-zinc-600 inline-block"/> Zwischenpfosten</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-blue-600 inline-block"/> Torpfosten / Tor</span>
            <span className="flex items-center gap-1.5 ml-auto text-zinc-400">Weitere Abschnitte erscheinen heller im Hintergrund.</span>
          </div>

          <section>
            <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-700 pb-2 mb-3 border-b border-zinc-200">Segmente &amp; Tore – {sec.name}</h2>
            <SegmentTable layout={layout} plan={sec} selectedSeg={selectedSeg} onSelectSeg={setSelectedSeg}
              onLength={(i, len) => mutSec((s) => { const dl = segmentsOf(deriveWerk(s).plan)[i]?.length ?? len; const drawn = segmentsOf(s)[i]?.length ?? len; return setSegmentLength(s, i, drawn + (len - dl)); })}
              onAngle={(i, a) => mutSec((s) => setVertexAngle(s, i, a))}
              onFlip={(i) => mutSec((s) => flipVertex(s, i))}
              onRemoveSegment={(i) => { mutSec((s) => removeSegment(s, i)); setSelectedSeg(null); }}
              onAddGate={(seg, offset) => upSec({ gates: [...sec.gates, { id: crypto.randomUUID(), seg, type: "gehtuere", width: GATE_DEFAULTS.gehtuere.width, offset }] })}
              onGate={(id, patch) => upSec({ gates: sec.gates.map((g) => (g.id === id ? { ...g, ...patch } : g)) })}
              onRemoveGate={(id) => upSec({ gates: sec.gates.filter((g) => g.id !== id) })} />
          </section>
          </>
          )}
        </div>

        <div className="space-y-4">
          <PlannerSettings plan={sec} pricelist={pricelist} onChange={upSec} />
          {isAnsicht ? <AnsichtPanel plan={sec} layout={layout} onChange={upSec} quoteId={quoteId} sectionLayouts={sectionLayouts} project={project} />
                     : <WerkPanel plan={sec} layout={layout} derived={derived} onChange={upSec} quoteId={quoteId} sectionLayouts={sectionLayouts} project={project} />}
          {inquiry && (
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2" data-testid="planner-inquiry-banner">
              Plan aus Anfrage <b>{inquiry.number}</b> ({inquiry.customer?.name}). Nach „Ins Angebot übernehmen“ werden Kunde und Plan ins Angebot übernommen.
            </div>
          )}
          <PlannerResult sectionLayouts={sectionLayouts} project={project} isAnsicht={isAnsicht} pricelist={pricelist} quoteId={quoteId} navigate={navigate} inquiry={inquiry} />
        </div>
      </div>
    </div>
  );
}

function SectionList({ project, sectionLayouts, onSelect, onAdd, onRemove, onRename }) {
  return (
    <section className="border border-zinc-200 bg-zinc-50/60 p-3" data-testid="planner-section-list">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-700 flex items-center gap-1.5"><Layers size={13}/> Abschnitte</h2>
        <button type="button" onClick={onAdd} data-testid="planner-add-section" className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 bg-zinc-900 text-white rounded-sm hover:bg-zinc-800"><Plus size={12}/> Neuer Abschnitt</button>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="planner-section-tabs">
        {project.sections.map((s, i) => {
          const len = sectionLayouts[i]?.layout?.totalLength || 0;
          const act = i === project.active;
          return (
            <div key={s.id} data-testid={`planner-section-${i}`}
                 className={`flex items-center gap-2 border rounded-sm pl-2 pr-1 py-1 cursor-pointer transition-colors ${act ? "border-zinc-900 bg-white ring-1 ring-zinc-900" : "border-zinc-300 bg-white hover:border-zinc-500"}`}
                 onClick={() => onSelect(i)}>
              <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: s.color }} />
              {act ? (
                <input value={s.name} onChange={(e) => onRename(e.target.value)} onClick={(e) => e.stopPropagation()} data-testid={`planner-section-name-${i}`}
                       className="text-xs font-semibold text-zinc-900 bg-transparent border-b border-dashed border-zinc-300 focus:outline-none focus:border-zinc-900 w-24" />
              ) : (
                <span className="text-xs font-semibold text-zinc-800" data-testid={`planner-section-label-${i}`}>{s.name}</span>
              )}
              <span className="text-[10px] font-mono text-zinc-500">{(len / 1000).toLocaleString("de-DE", { maximumFractionDigits: 2 })} m · {s.model}</span>
              <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(i); }} data-testid={`planner-section-remove-${i}`} className="text-zinc-400 hover:text-red-600 p-0.5" title="Abschnitt entfernen"><Trash2 size={12}/></button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ToolBtn({ active, onClick, icon: Icon, label, disabled, testId }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testId}
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 border rounded-sm transition-colors disabled:opacity-40 ${
              active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-800 border-zinc-300 hover:border-zinc-900"}`}>
      <Icon size={13}/> {label}
    </button>
  );
}

function PostPicker({ label, value, profiles, onChange, testId }) {
  const dims = profileDims(value.profil);
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-1">
        <select value={value.profil} onChange={(e) => onChange({ profil: e.target.value, dim: profileDims(e.target.value)[0] || 0 })} className={selCls} data-testid={`${testId}-profil`}>
          {profiles.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        {dims.length === 2 && dims[0] !== dims[1] && (
          <div className="flex shrink-0">
            {dims.map((d) => (
              <button key={d} type="button" onClick={() => onChange({ ...value, dim: d })} data-testid={`${testId}-dim-${d}`}
                      className={`px-2 text-xs font-mono border ${value.dim === d ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 text-zinc-700"}`}>{d}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PlannerSettings({ plan, pricelist, onChange }) {
  const models = Object.keys(pricelist.products).filter((k) => pricelist.products[k].zaunelement?.length);
  const variants = pricelist.products[plan.model]?.zaunelementVariants || [];
  const allProfiles = (pricelist.accessories?.pfosten?.profilTypes || []).map((p) => p.profil);
  const postless = isPostless(plan);
  const profiles = allowedPostProfiles(plan, allProfiles);
  const gateProfiles = gatePostProfiles(profiles);
  useEffect(() => {
    if (gateProfiles.length && !gateProfiles.includes(plan.posts.gate.profil)) onChange({ posts: { ...plan.posts, gate: { profil: gateProfiles[0], dim: profileDims(gateProfiles[0])[0] || 0 } } });
    // eslint-disable-next-line
  }, [postless, plan.posts.gate.profil]);
  const [postAuto, setPostAuto] = useState(plan.postHeight === Number(plan.height) + 60);
  const setHeight = (h) => onChange({ height: Number(h), ...(postAuto ? { postHeight: Number(h) + 60 } : {}) });
  return (
    <section className="border border-zinc-200 p-4 space-y-3 bg-zinc-50/40" data-testid="planner-settings">
      <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-700 pb-2 border-b border-zinc-200 flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ background: plan.color }} /> {plan.name} – Zaun &amp; Pfosten</h2>
      <div>
        <Label>Modell</Label>
        <select value={plan.model} data-testid="planner-model"
                onChange={(e) => { const v = pricelist.products[e.target.value]?.zaunelementVariants; onChange({ model: e.target.value, variant: v?.length ? v[0].name : null }); }} className={selCls}>
          {models.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      {variants.length > 0 && (
        <div>
          <Label>Ausführung</Label>
          <select value={plan.variant || ""} onChange={(e) => onChange({ variant: e.target.value })} className={selCls} data-testid="planner-variant">
            {variants.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
          </select>
        </div>
      )}
      <div className="grid grid-cols-3 gap-2">
        <div><Label>Zaunhöhe (mm)</Label><NumInput step={50} value={plan.height} onChange={(v) => setHeight(v)} className={numCls} testId="planner-height" /></div>
        <div><Label>Pfostenhöhe</Label><NumInput step={100} value={plan.postHeight} onChange={(v) => { setPostAuto(false); onChange({ postHeight: v }); }} className={numCls} testId="planner-post-height" /></div>
        {!postless && <div><Label>Max. Feld (mm)</Label><NumInput step={100} value={plan.maxField} emptyValue={2000} onChange={(v) => onChange({ maxField: v || 2000 })} className={numCls} testId="planner-max-field" /></div>}
      </div>
      {postless && <div className="text-xs text-zinc-700 bg-zinc-100 border border-zinc-200 px-2 py-1.5" data-testid="planner-postless-hint">{plan.model}: selbsttragend ohne Zwischen-/Eckpfosten – Zaun läuft durchgehend. Pfosten nur bei Toren ({gateProfiles.join(", ") || "120x120 / 150x150 / 200x100"}).</div>}
      {!postless && <PostPicker label="Zwischenpfosten (Maß in Zaunrichtung)" value={plan.posts.field} profiles={profiles} onChange={(v) => onChange({ posts: { ...plan.posts, field: v } })} testId="planner-post-field" />}
      {!postless && <PostPicker label="Eck- / Endpfosten" value={plan.posts.corner} profiles={profiles} onChange={(v) => onChange({ posts: { ...plan.posts, corner: v } })} testId="planner-post-corner" />}
      <PostPicker label="Torpfosten" value={plan.posts.gate} profiles={gateProfiles} onChange={(v) => onChange({ posts: { ...plan.posts, gate: v } })} testId="planner-post-gate" />
    </section>
  );
}

function PlannerResult({ sectionLayouts, project, isAnsicht, pricelist, quoteId, navigate, inquiry }) {
  const [priced, setPriced] = useState({ items: [], net: 0, loading: false, error: "" });
  const timer = useRef(null);
  const key = JSON.stringify(sectionLayouts.map(({ sec, layout }) => ({ f: layout.fields, p: layout.posts, g: layout.gates, m: sec.model, v: sec.variant, h: sec.height, ph: sec.postHeight })));

  const agg = useMemo(() => sectionLayouts.reduce((a, { layout }) => ({
    totalLength: a.totalLength + (layout.totalLength || 0), fieldCount: a.fieldCount + (layout.fieldCount || 0),
    postCount: a.postCount + (layout.postCount || 0), gateCount: a.gateCount + (layout.gates?.length || 0),
    segCount: a.segCount + (layout.segments?.length || 0), pfeilerCount: a.pfeilerCount + (layout.pfeilerCount || 0),
  }), { totalLength: 0, fieldCount: 0, postCount: 0, gateCount: 0, segCount: 0, pfeilerCount: 0 }), [sectionLayouts]);

  useEffect(() => {
    clearTimeout(timer.current);
    const anySeg = sectionLayouts.some((x) => x.layout.segments.length);
    if (!anySeg) { setPriced({ items: [], net: 0, loading: false, error: "" }); return; }
    timer.current = setTimeout(async () => {
      setPriced((s) => ({ ...s, loading: true, error: "" }));
      try {
        const mark = (cfg) => ({ ...cfg, fromPlanner: true });
        const priceSection = async ({ sec, layout }) => {
          const tag = project.sections.length > 1 ? `${sec.name} · ` : "";
          const gateGroups = {};
          for (const g of layout.gates) { const k = `${g.type}|${g.width}|${g.height || sec.height}`; gateGroups[k] = gateGroups[k] ? { ...gateGroups[k], count: gateGroups[k].count + 1, segs: [...gateGroups[k].segs, g.seg] } : { ...g, height: g.height || sec.height, count: 1, segs: [g.seg] }; }
          const [fieldRes, gateRes, postRes] = await Promise.all([
            Promise.all(layout.fields.map((f) => api.post("/calc/zaunelement", { productKey: sec.model, height: f.height || sec.height, width: f.width, quantity: f.count, variant: sec.variant || null }))),
            Promise.all(Object.values(gateGroups).map((g) => api.post("/calc/gate", { productKey: sec.model, gateType: g.type, height: g.height, width: g.width, quantity: g.count }))),
            Promise.all(layout.posts.map((p) => api.post("/calc/pfosten", { profil: p.profil, height: p.height || sec.postHeight, quantity: p.count })
              .catch((e) => ({ data: { unitPrice: 0, total: 0, note: formatApiError(e.response?.data?.detail) || "Kein Preis" } })))),
          ]);
          const items = [];
          layout.fields.forEach((f, i) => {
            const r = fieldRes[i];
            items.push({ type: "zaunelement", productKey: sec.model, description: `${sec.model} Zaunelement`,
              details: `${tag}B ${f.width} × H ${f.height || sec.height} mm, ${f.count} Stk.${sec.variant ? " — inkl. " + sec.variant : ""} (Zaunplaner)`,
              config: mark({ height: f.height || sec.height, width: f.width, schraeg: false, variant: sec.variant || null, option: null }),
              quantity: f.count, unitPrice: r.data.unitPrice || 0, total: r.data.total || 0 });
          });
          Object.values(gateGroups).forEach((g, i) => {
            const r = gateRes[i];
            const label = GATE_DEFAULTS[g.type].label;
            items.push({ type: g.type, productKey: sec.model, description: `${sec.model} ${label}`,
              details: `${tag}B ${g.width} × H ${g.height} mm, ${g.count} Stk. (${g.segs.join(", ")})`,
              config: mark({ height: g.height, width: g.width, gateType: g.type, tueranschlag: false, zylinder: false, doppelstabmatte: false }),
              quantity: g.count, unitPrice: r.data.unitPrice || 0, total: r.data.total || 0, note: r.data.note });
            if (r.data.maxiSystemAmount > 0) {
              items.push({ type: "custom", productKey: sec.model, description: r.data.maxiSystemName,
                details: `${tag}für ${sec.model} ${label} B ${g.width} mm, ${g.count} Stk.`, config: mark({ maxiSystem: true, gateWidth: g.width }),
                quantity: g.count, unitPrice: r.data.maxiSystemAmount, total: r.data.maxiSystemTotal });
            }
          });
          layout.posts.forEach((p, i) => {
            const r = postRes[i];
            const ph = p.height || sec.postHeight;
            items.push({ type: "accessory", productKey: "pfosten", description: `Pfosten ${p.profil}`, details: `${tag}Höhe ${ph} mm, ${p.count} Stk. (Zaunplaner)`,
              config: mark({ profil: p.profil, height: ph, winkelschnitt: false, seitenmontageplatte: false }),
              quantity: p.count, unitPrice: r.data.unitPrice || 0, total: r.data.total || 0, note: r.data.note });
          });
          return items;
        };
        const perSection = await Promise.all(sectionLayouts.filter((x) => x.layout.segments.length).map(priceSection));
        const items = perSection.flat();
        setPriced({ items, net: items.reduce((a, i) => a + (i.total || 0), 0), loading: false, error: "" });
      } catch (e) {
        setPriced((s) => ({ ...s, loading: false, error: formatApiError(e.response?.data?.detail) || e.message }));
      }
    }, 400);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line
  }, [key]);

  const warnings = sectionLayouts.flatMap(({ sec, layout }) => (layout.warnings || []).map((w) => (project.sections.length > 1 ? `${sec.name}: ${w}` : w)));

  const transfer = () => {
    const prefillItems = priced.items.map(({ note, ...i }) => i);
    const segments = [];
    sectionLayouts.forEach(({ layout }) => layout.segments.forEach((s) => segments.push({ idx: segments.length, label: s.label, a: s.a, b: s.b, length: s.length, elements: s.elements })));
    const sketch = { segments, totalLength: agg.totalLength, fieldCount: agg.fieldCount, postCount: agg.postCount };
    const first = project.sections[0];
    const planner = { ...project, model: first.model, height: first.height, points: first.points, ansicht: first.ansicht, sketch };
    navigate(quoteId ? `/quote/${quoteId}` : "/quote/new", { state: { prefillItems, planner, inquiry } });
  };

  return (
    <section className="border border-zinc-200 p-4 space-y-3" data-testid="planner-result">
      <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-700 pb-2 border-b border-zinc-200">Ergebnis (alle Abschnitte)</h2>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <Stat label="Gesamtlänge" value={`${agg.totalLength.toLocaleString("de-DE")} mm`} testId="planner-total-length" />
        {isAnsicht ? <Stat label="Pfeiler (Beton)" value={agg.pfeilerCount} testId="planner-pfeiler-count" /> : <Stat label="Segmente" value={agg.segCount} testId="planner-seg-count" />}
        <Stat label="Zaunfelder" value={agg.fieldCount} testId="planner-field-count" />
        <Stat label="Pfosten" value={agg.postCount} testId="planner-post-count" />
        <Stat label="Tore" value={agg.gateCount} testId="planner-gate-count" />
        <Stat label="Netto (ohne Rabatt)" value={priced.loading ? "…" : fmtEUR(priced.net)} testId="planner-net" strong />
      </div>
      {warnings.length > 0 && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1.5 space-y-0.5" data-testid="planner-warnings">
          {warnings.map((w, i) => <div key={i} className="flex gap-1.5"><AlertTriangle size={12} className="mt-0.5 shrink-0"/>{w}</div>)}
        </div>
      )}
      {priced.error && <div className="text-xs text-red-600" data-testid="planner-price-error">{priced.error}</div>}
      {priced.items.length > 0 && (
        <div className="divide-y divide-zinc-100 border border-zinc-200 text-xs" data-testid="planner-items">
          {priced.items.map((it, i) => (
            <div key={i} className="px-2 py-1.5 flex justify-between gap-2" data-testid={`planner-item-${i}`}>
              <div className="min-w-0">
                <div className="font-medium text-zinc-900 truncate">{it.description}</div>
                <div className="text-zinc-500 truncate">{it.details}{it.note ? ` · ${it.note}` : ""}</div>
              </div>
              <div className="font-mono text-zinc-800 whitespace-nowrap">{fmtEUR(it.total)}</div>
            </div>
          ))}
        </div>
      )}
      <button onClick={transfer} disabled={!priced.items.length || priced.loading} data-testid="planner-transfer-button"
              className="w-full bg-zinc-900 text-white text-sm font-medium px-4 py-2.5 rounded-sm hover:bg-zinc-800 disabled:opacity-40 flex items-center justify-center gap-2">
        <FileCheck2 size={14}/> {quoteId ? "Positionen im Angebot aktualisieren" : "Ins Angebot übernehmen"}
      </button>
      <p className="text-[11px] text-zinc-500">{isAnsicht ? "Betonpfeiler sind bauseits und nicht in der Stückliste. Felder unter 1000 mm werden mit 1000 mm berechnet." : "Eckpfosten werden pro Abschnitt gezählt. Feldbreiten auf ganze mm gerundet. Felder unter 1000 mm werden mit 1000 mm berechnet."}</p>
    </section>
  );
}

function Stat({ label, value, testId, strong }) {
  return (
    <div className="bg-zinc-50 border border-zinc-200 px-2.5 py-2">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-mono ${strong ? "text-base font-semibold" : "text-sm"} text-zinc-900`} data-testid={testId}>{value}</div>
    </div>
  );
}
